import React, { useState, useEffect } from 'react';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { useParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import CargomaticTab from 'components/v2/navs/Tab';
import { useTranslation } from 'react-i18next';
import {
  useExpiredQuickPayContracts,
  useQuickPayContracts,
  useTermsAndConditions
} from 'hooks/quick-pay/useContracts';
import { isProd } from 'helpers/helper_functions';
import Sidebar from '../../../carrier/AdminSidebar';
import Active from './Active';
import Expired from './Expired';
import History from './History';
import { getCustomerV2 } from '../../../../../utils/CustomersAPIUtils';

const QuickPay = () => {
  const { id: carrierId } = useParams();
  const { t } = useTranslation();
  const [carrier, setCarrier] = useState(null);
  const [activeContracts, setActiveContracts] = useState([]);
  const [scheduledContracts, setScheduledContracts] = useState([]);

  const { data: termsAndConditions, isLoading: termsAndConditionsLoading } =
    useTermsAndConditions();

  const {
    data,
    isSuccess,
    isLoading: activeLoading,
  } = useQuickPayContracts(carrierId);

  const { data: expiredContracts, isLoading: expiredLoading } =
    useExpiredQuickPayContracts(carrierId);

  const fetchUser = async () => {
    const user = await getCustomerV2(carrierId);
    setCarrier(user);
  };

  const populateData = (contractData) => {
    const contracts = contractData.reduce(
      (acc, contract) => {
        if (contract.type === 'active') {
          acc.active = contract.items;
        }
        if (contract.type === 'scheduled') {
          acc.scheduled = contract.items;
        }
        return acc;
      },
      { active: [], scheduled: [] },
    );

    if (contracts.active) {
      setActiveContracts(contracts.active);
    }
    if (contracts.scheduled) {
      setScheduledContracts(contracts.scheduled);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (isSuccess && data) {
      populateData(data);
    }
  }, [data]);

  const tabs = [
    {
      slug: 'active',
      name: t('ACTIVE'),
    },
    {
      slug: 'expired',
      name: t('EXPIRED'),
    },
    {
      slug: 'history',
      name: t('HISTORY'),
    },
  ];

  const renderSidebar = () => (
    <Sidebar userId={carrierId} activePage="quickpay" />
  );

  const breadcrumbs = {
    pages: [
      {
        name: carrier?.name || t('CARRIER'),
        href: `/users/${carrierId}`,
        current: false,
      },
      {
        name: 'QuickPay',
        href: `/users/${carrierId}/carrier-settings/quick-pay`,
        current: true,
      },
    ],
  };
  return (
    <LayoutWithSidebar
      sidebar={renderSidebar}
      breadcrumbs={breadcrumbs}
      className="py-4"
    >
      <div className="mb-6 col-span-1" data-testid="quick-pay-page">
        <Tab.Group>
          <Tab.List>
            <div className="space-x-4">
              {tabs.map((tab) => (
                <CargomaticTab tab={tab} />
              ))}
            </div>
          </Tab.List>
          <Tab.Panels className="mt-6">
            <Tab.Panel>
              <Active
                id={carrierId}
                activeContracts={activeContracts}
                scheduledContracts={scheduledContracts}
                loading={activeLoading || termsAndConditionsLoading}
                termsAndConditions={termsAndConditions?.items}
              />
            </Tab.Panel>

            <Tab.Panel>
              <Expired
                id={carrierId}
                data={expiredContracts}
                loading={expiredLoading}
              />
            </Tab.Panel>
            {!isProd() && (
              <Tab.Panel>
                <History />
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </LayoutWithSidebar>
  );
};

export default QuickPay;
